import React, { useState, useEffect } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import rootAxios from 'requests/rootAxios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`regions-circles-tabpanel-${index}`}
      aria-labelledby={`regions-circles-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const RegionsCircles = () => {
  const [tabValue, setTabValue] = useState(0);
  const [regions, setRegions] = useState([]);
  const [circles, setCircles] = useState([]);
  const [filteredRegions, setFilteredRegions] = useState([]);
  const [filteredCircles, setFilteredCircles] = useState([]);
  const [filteredBranches, setFilteredBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialog, setEditDialog] = useState({
    open: false,
    type: null, // 'region' or 'circle'
    data: null,
    originalData: null,
  });

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0); // Reset pagination when switching tabs
    setSearchTerm(''); // Reset search when switching tabs
  };

  // Fetch data based on type
  const fetchData = async (type) => {
    try {
      setLoading(true);
      setError(null);

      const response = await rootAxios.get(`user/get-rms-and-chs/${type}`);

      if (response?.data?.status === 'success') {
        if (type === 'rm') {
          setRegions(response.data.data || []);
          setFilteredRegions(response.data.data || []);
        } else if (type === 'ch') {
          const circlesData = response.data.data || [];
          // Keep only unique circle IDs, ignoring region IDs
          const uniqueCircles = circlesData.filter(
            (circle, index, self) =>
              index === self.findIndex((c) => c.CIRCLE_ID === circle.CIRCLE_ID)
          );
          setCircles(uniqueCircles);
          setFilteredCircles(uniqueCircles);
        } else if (type === 'br') {
          setFilteredBranches(response.data.data || []);
        }
      } else {
        throw new Error(response?.data?.message || 'Failed to fetch data');
      }
    } catch (err) {
      console.error(`Error fetching ${type} data:`, err);
      setError(err.message || `Failed to fetch ${type} data`);
      toast.error(`Failed to fetch ${type} data`);
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts and tab changes
  useEffect(() => {
    const type = tabValue === 0 ? 'rm' : tabValue === 1 ? 'ch' : 'br';
    fetchData(type);
  }, [tabValue]);

  // Filter data based on search term
  useEffect(() => {
    const currentData = tabValue === 0 ? regions : circles;
    const filtered = currentData.filter((item) => {
      if (tabValue === 0) {
        // Filter regions
        return (
          item.regionId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.regionName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.CIRCLE_ID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.RM_ID?.toString().includes(searchTerm) ||
          item.EMAIL_ID?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      } else {
        // Filter circles
        return (
          item.CIRCLE_ID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.CIRCLE_NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.CIRCLE_MANAGER_ID?.toString().includes(searchTerm) ||
          item.REGION_ID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.CIRCLE_OFFICIAL_EMAIL?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
    });

    if (tabValue === 0) {
      setFilteredRegions(filtered);
    } else {
      setFilteredCircles(filtered);
    }
    setPage(0); // Reset to first page when filtering
  }, [searchTerm, tabValue, regions, circles]);

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page data
  const getCurrentPageData = () => {
    const data =
      tabValue === 0
        ? filteredRegions
        : tabValue === 1
          ? filteredCircles
          : tabValue === 2
            ? filteredBranches
            : [];
    const startIndex = page * rowsPerPage;
    return data.slice(startIndex, startIndex + rowsPerPage);
  };

  // Open edit dialog
  const handleEdit = (type, data) => {
    setEditDialog({
      open: true,
      type,
      data: { ...data },
      originalData: { ...data },
    });
  };

  // Close edit dialog
  const handleCloseEdit = () => {
    setEditDialog({
      open: false,
      type: null,
      data: null,
      originalData: null,
    });
  };

  // Handle input changes in edit dialog
  const handleEditChange = (field, value) => {
    setEditDialog((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value,
      },
    }));
  };

  // Save changes
  const handleSave = async () => {
    try {
      const { type, data } = editDialog;

      // Validate required fields
      if (type === 'region') {
        if (!data.RM_ID || !data.EMAIL_ID) {
          toast.error('RM ID and Email are required');
          return;
        }
      } else if (type === 'circle') {
        if (!data.CIRCLE_MANAGER_ID || !data.CIRCLE_OFFICIAL_EMAIL || !data.CIRCLE_NAME) {
          toast.error('Circle Manager ID, Circle Name and Email are required');
          return;
        }
      }

      // Here you would typically make an API call to update the data
      // For now, we'll just update the local state
      if (type === 'region') {
        setRegions((prev) =>
          prev.map((region) => (region._id.$oid === data._id.$oid ? data : region))
        );
      } else if (type === 'circle') {
        setCircles((prev) =>
          prev.map((circle) => (circle._id.$oid === data._id.$oid ? data : circle))
        );
      }
      // update keys
      let payload = {
        regionId: data.regionId,
        circleId: data.CIRCLE_ID,
        rmId: data.RM_ID,
        rmEmailId: data.EMAIL_ID,
        circleManagerId: data.CIRCLE_MANAGER_ID,
        circleOfficialEmail: data.CIRCLE_OFFICIAL_EMAIL,
        circleName: data.CIRCLE_NAME,
        type: type,
        _id: data._id,
      };
      await rootAxios.post(`/user/update-regions-and-circles`, payload);
      toast.success(`${type === 'region' ? 'Region' : 'Circle'} updated successfully`);
      handleCloseEdit();
    } catch (err) {
      console.error('Error saving changes:', err);
      toast.error('Failed to save changes');
    }
  };

  // Cancel changes
  const handleCancel = () => {
    setEditDialog((prev) => ({
      ...prev,
      data: { ...prev.originalData },
    }));
    handleCloseEdit();
  };

  // Get unique ID for table rows
  const getUniqueId = (item) => {
    return item._id?.$oid || item._id || Math.random().toString();
  };

  // Render regions table
  const renderRegionsTable = () => (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Region ID</TableCell>
              <TableCell>Region Name</TableCell>
              <TableCell>Circle ID</TableCell>
              <TableCell>RM ID</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getCurrentPageData().map((region) => (
              <TableRow key={getUniqueId(region)}>
                <TableCell>{region.regionId}</TableCell>
                <TableCell>{region.regionName}</TableCell>
                <TableCell>{region.CIRCLE_ID}</TableCell>
                <TableCell>{region.RM_ID}</TableCell>
                <TableCell>{region.EMAIL_ID}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleEdit('region', region)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredRegions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );

  // Render circles table
  const renderCirclesTable = () => (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Circle ID</TableCell>
              <TableCell>Circle Name</TableCell>
              <TableCell>Circle Manager ID</TableCell>
              {/* <TableCell>Region ID</TableCell> */}
              <TableCell>Email</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getCurrentPageData().map((circle) => (
              <TableRow key={getUniqueId(circle)}>
                <TableCell>{circle.CIRCLE_ID}</TableCell>
                <TableCell>{circle.CIRCLE_NAME}</TableCell>
                <TableCell>{circle.CIRCLE_MANAGER_ID}</TableCell>
                {/* <TableCell>{circle.REGION_ID}</TableCell> */}
                <TableCell>{circle.CIRCLE_OFFICIAL_EMAIL}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleEdit('circle', circle)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredCircles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );

  // Render branches table
  const renderBranchesTable = () => (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Branch ID</TableCell>
              <TableCell>Branch Name</TableCell>
              <TableCell>Region ID</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getCurrentPageData().map((branch) => (
              <TableRow key={getUniqueId(branch)}>
                <TableCell>{branch.BRANCH_ID}</TableCell>
                <TableCell>{branch.BRANCH_NAME}</TableCell>
                <TableCell>{branch.REGION_ID}</TableCell>
                <TableCell>{branch.ACTIVE}</TableCell>
                <TableCell>{branch.EMAIL_ID}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredCircles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );

  // Render edit dialog
  const renderEditDialog = () => {
    const { open, type, data } = editDialog;

    if (!open || !data) return null;

    return (
      <Dialog open={open} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
        <DialogTitle>Edit {type === 'region' ? 'Region' : 'Circle'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {type === 'region' ? (
              <>
                <TextField
                  fullWidth
                  label="Region ID"
                  value={data.regionId || ''}
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Region Name"
                  value={data.regionName || ''}
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Circle ID"
                  value={data.CIRCLE_ID || ''}
                  onChange={(e) => handleEditChange('CIRCLE_ID', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="RM ID"
                  value={data.RM_ID || ''}
                  onChange={(e) => handleEditChange('RM_ID', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={data.EMAIL_ID || ''}
                  onChange={(e) => handleEditChange('EMAIL_ID', e.target.value)}
                  type="email"
                />
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Circle ID"
                  value={data.CIRCLE_ID || ''}
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Circle Name"
                  value={data.CIRCLE_NAME || ''}
                  // disabled
                  onChange={(e) => handleEditChange('CIRCLE_NAME', e.target.value)}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Circle Manager ID"
                  value={data.CIRCLE_MANAGER_ID || ''}
                  onChange={(e) => handleEditChange('CIRCLE_MANAGER_ID', e.target.value)}
                  type="number"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Region ID"
                  value={data.REGION_ID || ''}
                  disabled
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Email"
                  value={data.CIRCLE_OFFICIAL_EMAIL || ''}
                  onChange={(e) => handleEditChange('CIRCLE_OFFICIAL_EMAIL', e.target.value)}
                  type="email"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Typography
            variant="h5"
            fontWeight="600"
            sx={{
              color: '#1e293b',
              mb: 0.25,
            }}
          >
            Regions & Circles Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
            Manage regional managers and circle heads across your organization
          </Typography>
        </Box>

        <Paper sx={{ width: '100%', mt: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="regions and circles tabs"
            width="2/3"
          >
            <Tab label="Regions" />
            <Tab label="Circles" />
            <Tab label="Branches" />
          </Tabs>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const data =
                  tabValue === 0
                    ? filteredRegions
                    : tabValue === 1
                      ? filteredCircles
                      : filteredBranches;
                const csvContent =
                  'data:text/csv;charset=utf-8,' +
                  [
                    Object.keys(data[0]).join(','),
                    ...data.map((row) => Object.values(row).join(',')),
                  ].join('\n');

                const encodedUri = encodeURI(csvContent);
                const link = document.createElement('a');
                link.setAttribute('href', encodedUri);
                link.setAttribute(
                  'download',
                  `${tabValue === 0 ? 'regions' : tabValue === 1 ? 'circles' : 'branches'}.csv`
                );
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <CloudDownloadIcon sx={{ mr: 1 }} />
              Download {tabValue === 0 ? 'Regions' : tabValue === 1 ? 'Circles' : 'Branches'} CSV
            </Button>
          </Box>
        </Paper>

        {/* Search Bar */}
        <Box sx={{ mt: 3, mb: 2 }}>
          <TextField
            fullWidth
            placeholder={`Search ${tabValue === 0 ? 'regions' : 'circles'}...`}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h6" gutterBottom>
                Regions ({filteredRegions.length})
              </Typography>
              {filteredRegions.length > 0 ? (
                renderRegionsTable()
              ) : (
                <Typography color="textSecondary">
                  {searchTerm ? 'No regions found matching your search' : 'No regions found'}
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Typography variant="h6" gutterBottom>
                Circles ({filteredCircles.length})
              </Typography>
              {filteredCircles.length > 0 ? (
                renderCirclesTable()
              ) : (
                <Typography color="textSecondary">
                  {searchTerm ? 'No circles found matching your search' : 'No circles found'}
                </Typography>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              {/* Brach Tab  */}
              <Typography variant="h6" gutterBottom>
                Branches ({filteredBranches.length})
              </Typography>
              {filteredBranches.length > 0 ? (
                renderBranchesTable()
              ) : (
                <Typography color="textSecondary">
                  {searchTerm ? 'No Branches found matching your search' : 'No branches found'}
                </Typography>
              )}
            </TabPanel>
          </>
        )}

        {renderEditDialog()}
      </Box>
    </Layout>
  );
};

export default RegionsCircles;
